import React, { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { getSkinTexture, getShirtTexture, getPantsTexture } from "./textures";

interface NPCPersonProps {
  center: [number, number, number];
  radius: number;
  speed: number;
  phase: number;
  shirtTint?: string;
}

/** A small decorative, non-physical person that patrols back and forth — used to
 * make the flagship project feel like a live, visited place. */
const NPCPerson: React.FC<NPCPersonProps> = ({ center, radius, speed, phase, shirtTint }) => {
  const groupRef = useRef<THREE.Group>(null);
  const leftLegRef = useRef<THREE.Mesh>(null);
  const rightLegRef = useRef<THREE.Mesh>(null);

  const skin = useMemo(() => getSkinTexture(), []);
  const shirt = useMemo(() => getShirtTexture(), []);
  const pants = useMemo(() => getPantsTexture(), []);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime() * speed + phase;
    const x = center[0] + Math.sin(t) * radius;
    const z = center[2] + Math.cos(t * 0.6) * radius * 0.6;
    if (groupRef.current) {
      groupRef.current.position.set(x, center[1], z);
      // Face along the direction of travel
      const dx = Math.cos(t) * radius;
      const dz = -Math.sin(t * 0.6) * radius * 0.6 * 0.6;
      groupRef.current.rotation.y = Math.atan2(-dx, -dz);
    }
    const swing = Math.sin(clock.getElapsedTime() * 8) * 0.4;
    if (leftLegRef.current) leftLegRef.current.rotation.x = swing;
    if (rightLegRef.current) rightLegRef.current.rotation.x = -swing;
  });

  return (
    <group ref={groupRef} scale={0.85}>
      <mesh position={[0, 1.35, 0]} castShadow>
        <boxGeometry args={[0.32, 0.32, 0.32]} />
        <meshStandardMaterial map={skin} roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.95, 0]} castShadow>
        <boxGeometry args={[0.44, 0.48, 0.26]} />
        <meshStandardMaterial map={shirt} color={shirtTint} roughness={1} />
      </mesh>
      <mesh ref={leftLegRef} position={[-0.12, 0.4, 0]} castShadow>
        <boxGeometry args={[0.16, 0.55, 0.16]} />
        <meshStandardMaterial map={pants} roughness={1} />
      </mesh>
      <mesh ref={rightLegRef} position={[0.12, 0.4, 0]} castShadow>
        <boxGeometry args={[0.16, 0.55, 0.16]} />
        <meshStandardMaterial map={pants} roughness={1} />
      </mesh>
    </group>
  );
};

export default NPCPerson;
